/**
 * 护理方案类型定义
 * Requirements: 16.1, 16.4, 16.5
 */

// ============================================
// 解锁状态
// ============================================

export interface UnlockStatus {
  is_unlocked: boolean;
  unlocked_at: string | null;
  care_started: boolean;
  care_started_at: string | null;
  current_day: number;
  hours_until_next_day: number | null;
}

// ============================================
// 分析结果
// ============================================

export type RiskLevel = "low" | "medium" | "high";

export interface AnalysisResult {
  condition_name_display: string;
  condition_code: string;
  similarity_score: number;
  risk_level: RiskLevel;
  risk_level_display: string;
}

// ============================================
// 上下文信息
// ============================================

export type WaterType = "freshwater" | "saltwater";

export interface CareContext {
  tank_volume_liters: number;
  tank_volume_display: string;
  is_volume_estimated: boolean;
  water_type: WaterType;
}

// ============================================
// 准备度
// ============================================

export interface ToolboxItem {
  name: string;
  owned: boolean;
  slug?: string;
}

export interface Readiness {
  score: number;
  matched_items: ToolboxItem[];
  missing_items: ToolboxItem[];
  encouragement_text: string | null;
}

// ============================================
// 即时行动
// ============================================

export interface LockedValue<T> {
  value: T | null;
  unit: string;
  display: string | null;
  preview_display: string;
}

export interface TargetTemperature extends LockedValue<number> {}

export interface EnvironmentSetup {
  target_temperature: TargetTemperature;
  equipment_warnings: string[];
}

export interface Dosage extends LockedValue<number> {}

export interface ProductInfo {
  name: string;
  slug: string;
  dosage: Dosage;
  instruction: string | null;
  instruction_preview: string;
  owned: boolean;
  safety_validated: boolean;
}

export interface ProductSetup {
  primary: ProductInfo | null;
  substitutes_used: ProductInfo[];
  substitute_reason: string | null;
}

export interface ImmediateActions {
  environment_setup: EnvironmentSetup;
  product_setup: ProductSetup;
}

// ============================================
// 每日例程
// ============================================

export interface TimelineTask {
  id: string;
  action: string;
  action_detail?: string | null;
  completed: boolean;
  has_locked_value?: boolean;
}

export interface DailyRoutineDay {
  day: number;
  day_label: string;
  is_observation_period?: boolean;
  tasks: TimelineTask[];
}

// ============================================
// Fission Engine 每日清单 (7 日裂变结构)
// ============================================

export type ActionType = "critical" | "observation" | "routine";

export interface DailyTask {
  id: string;
  original_step_id: string;
  day_index: number;
  action: string;
  product_key?: string | null;
  action_type: ActionType;
  is_continued: boolean;
  is_emergency: boolean;
  badge?: string | null;
  priority_score: number;
  status: "pending" | "completed" | "skipped";
}

export interface DailyScheduleDay {
  day_index: number;
  day: number;
  title: string;
  title_key?: string;
  day_label: string;
  is_emergency: boolean;
  is_current: boolean;
  date_display: string;
  tasks: DailyTask[];
  tasks_count: number;
  is_observation_period: boolean;
}

/** 判断是否为 Fission 结构 */
export function isFissionSchedule(
  routine: DailyRoutineDay[] | DailyScheduleDay[],
): routine is DailyScheduleDay[] {
  return routine.length > 2 && "day_index" in routine[0];
}

// ============================================
// 应急措施
// ============================================

export interface Contingency {
  escalation_triggers: string[];
  actions: string[];
}

// ============================================
// 护理时间轴（主类型）
// ============================================

export interface CareTimeline {
  assessment_id: string;
  condition_code: string;
  unlock_status: UnlockStatus;
  analysis_result: AnalysisResult;
  context: CareContext;
  readiness: Readiness;
  immediate_actions: ImmediateActions;
  daily_routine: DailyRoutineDay[];
  contingency: Contingency;
  care_duration_days: number;
  disclaimer: string;
  error?: string;
}

// ============================================
// 护理日志
// ============================================

export type LogType = "task_completed" | "observation" | "note";
export type ObservationStatus = "improved" | "unchanged" | "worsened";

export interface CareLog {
  id: number;
  log_type: LogType;
  observation_status: ObservationStatus | null;
  day_number: number;
  task_data: Record<string, unknown> | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CareLogsMeta {
  current_day: number;
  hours_until_next_day: number | null;
  tasks_completed_today: number;
  total_tasks_completed: number;
  consecutive_worsened_days: number;
  care_started: boolean;
  care_completed: boolean;
}

// 注意：这些类型是 userApiClient 解包后的 data 字段内容
// userApiClient 会自动从 { success: true, data: ..., meta: {} } 中提取 data

export interface CareLogsResponse {
  care_logs: CareLog[];
  meta: CareLogsMeta;
}

// ============================================
// 升级警报
// ============================================

export interface EscalationAlert {
  triggered: boolean;
  consecutive_worsened_days: number;
  message: string;
  actions: string[];
}

// ============================================
// API 请求/响应类型
// ============================================

// 注意：这些类型是 userApiClient 解包后的 data 字段内容

export interface CreateCareLogRequest {
  care_log: {
    log_type: LogType;
    observation_status?: ObservationStatus;
    day_number?: number;
    task_data?: Record<string, unknown>;
    notes?: string;
  };
}

export interface CreateCareLogResponse {
  care_log: CareLog;
  meta: CareLogsMeta;
  escalation_alert?: EscalationAlert;
}

// 注意：这些类型是 userApiClient 解包后的 data 字段内容
// userApiClient 会自动从 { success: true, data: ..., meta: {} } 中提取 data

export interface UnlockCareResponse {
  message: string;
  care_timeline: CareTimeline;
}

export interface StartCareResponse {
  message: string;
  care_timeline: CareTimeline;
}

export interface CompleteCareRequest {
  outcome: "recovered" | "improved" | "unchanged" | "lost" | "unknown";
}

export interface CompleteCareResponse {
  message: string;
  outcome: string;
  care_timeline: CareTimeline;
}

// ============================================
// 评估响应（包含护理时间轴）
// ============================================

export interface AssessmentWithTimeline {
  id: number;
  friendly_id: string;
  status: string;
  detected_disease_code: string | null;
  image_url?: string;
  thumb_url?: string;
  care_timeline?: CareTimeline;
  // ... 其他评估字段
}
